<template>
  <div class="blog-page">
    <Top />

    <section class="page-hero" aria-label="Blog header">
      <div class="container page-hero-inner">
        <h1 class="page-hero-title">BLOG</h1>
      </div>
    </section>

    <section class="blog" aria-label="Blog posts">
      <div class="container blog-inner">
        <div v-if="listLoading" class="state-msg">Loading…</div>
        <div v-else-if="listError" class="state-msg state-err">{{ listError }}</div>
        <div v-else-if="!displayItems.length" class="state-msg">No published posts yet.</div>
        <div v-else class="grid">
          <article
            v-for="item in displayItems"
            :key="'blog-' + item.id"
            class="card card--clickable"
            role="button"
            tabindex="0"
            @click="goDetail(item.id)"
            @keydown.enter.prevent="goDetail(item.id)"
          >
            <div class="thumb">
              <img class="thumb-img" :src="item.img" :alt="item.title" loading="lazy" />
            </div>
            <div class="body">
              <div class="meta-line">{{ item.metaLine }}</div>
              <h3 class="title">{{ item.title }}</h3>
              <p class="desc">{{ item.desc }}</p>
              <a class="read-more" href="#" @click.stop.prevent="goDetail(item.id)">read all..</a>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="cta-band">
      <div class="container cta-inner">
        <div class="cta-left">
          <h2 class="cta-title">AESTHETICS PRACTITIONER?</h2>
          <p class="cta-sub">
            Grow your business with DOSE and access new patients, forms, diary, reminders<br />
            and no show/late cancellation protection.
          </p>
        </div>
        <div class="cta-right">
          <button class="cta-btn" type="button" @click="goAbout">LEARN MORE</button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import Footer from '../components/footer.vue'
import Top from '../components/top.vue'
import { fetchPublicBlogList } from '../api/blog'
import { resolveAssetUrl } from '../utils/asset'

export default {
  name: 'BlogPage',
  components: {
    Footer,
    Top,
  },
  data() {
    return {
      listLoading: true,
      listError: '',
      listRows: [],
      defaultThumb: require('../assets/images/newImg.jpg'),
    }
  },
  computed: {
    displayItems() {
      return (this.listRows || []).map((row) => ({
        id: row.id,
        title: (row.title && String(row.title).trim()) || '',
        img: this.cardImage(row),
        desc: this.excerpt(row.content),
        metaLine: this.metaLine(row),
      }))
    },
  },
  mounted() {
    this.loadList()
  },
  methods: {
    cardImage(row) {
      const url = row.coverImageUrl || row.cover_image_url
      const resolved = resolveAssetUrl(url)
      return resolved || this.defaultThumb
    },
    stripHtml(raw) {
      if (raw == null) return ''
      let s = String(raw)
      s = s.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
      s = s.replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
      s = s.replace(/<[^>]+>/g, ' ')
      s = s.replace(/&nbsp;/gi, ' ')
      s = s.replace(/\u00a0/g, ' ')
      return s.replace(/\s+/g, ' ').trim()
    },
    excerpt(html) {
      const text = this.stripHtml(html)
      if (!text) return ''
      const max = 180
      if (text.length <= max) return text
      return text.slice(0, max) + '…'
    },
    formatPublished(value) {
      if (value == null || value === '') return ''
      if (typeof value === 'string') {
        const s = value.trim()
        const d = new Date(s)
        if (!Number.isNaN(d.getTime())) {
          return this.formatDateEn(d)
        }
        return s.length >= 10 ? s.slice(0, 10) : s
      }
      try {
        const d = new Date(value)
        if (Number.isNaN(d.getTime())) return ''
        return this.formatDateEn(d)
      } catch {
        return ''
      }
    },
    formatDateEn(d) {
      const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ]
      return `${months[d.getMonth()]} ${d.getDate()} ${d.getFullYear()}`
    },
    metaLine(row) {
      const date = this.formatPublished(row.publishedAt || row.published_at)
      const pub = row.publisher != null ? String(row.publisher).trim() : ''
      if (date && pub) return `${date} · ${pub}`
      return date || pub || ''
    },
    loadList() {
      this.listLoading = true
      this.listError = ''
      fetchPublicBlogList()
        .then((list) => {
          this.listRows = list || []
        })
        .catch((e) => {
          this.listError = (e && e.message) || 'Failed to load blog'
          this.listRows = []
        })
        .finally(() => {
          this.listLoading = false
        })
    },
    goDetail(id) {
      if (id == null || id === '') return
      this.$router.push({ name: 'blogDetail', params: { id: String(id) } }).catch((err) => {
        if (err && err.name !== 'NavigationDuplicated') throw err
      })
    },
    goAbout() {
      this.$router.push({ name: 'about' }).catch((err) => {
        if (err && err.name !== 'NavigationDuplicated') throw err
      })
    },
  },
}
</script>

<style scoped>
.blog-page {
  min-height: 100vh;
  background: #ffffff;
  color: #0c0b06;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-hero {
  background: #f4f3ef;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.page-hero-inner {
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-hero-title {
  margin: 0;
  font-size: 16px;
  letter-spacing: 0.16em;
  font-weight: 500;
  color: #0c0b06;
}

.blog-inner {
  padding: 44px 0 56px;
}

.state-msg {
  padding: 40px 0;
  text-align: center;
  color: #0c0b06;
  font-size: 13px;
}

.state-err {
  color: #a94442;
}

.grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
}

.card {
  background: #fff;
}

.card--clickable {
  cursor: pointer;
  text-align: left;
}

.card--clickable:focus-visible {
  outline: 2px solid rgba(176, 138, 74, 0.65);
  outline-offset: 2px;
}

.thumb {
  background: #f3f3f3;
  overflow: hidden;
  aspect-ratio: 4 / 3;
}

.thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transform: scale(1.001);
  transition: transform 180ms ease;
}

@media (hover: hover) and (pointer: fine) {
  .card:hover .thumb-img {
    transform: scale(1.03);
  }
}

.body {
  padding: 14px 2px 0;
}

.meta-line {
  font-size: 12px;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.35);
}

.title {
  margin: 6px 0 0;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: 0.04em;
  color: #0c0b06;
  font-weight: initial;
}

.desc {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.65;
  color:#0c0b06;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.read-more {
  display: inline-block;
  margin-top: 10px;
  font-size: 12px;
  color: #b08a4a;
  text-decoration: none;
}

.read-more:hover {
  color: #b08a4a;
}

.cta-band {
  padding: 44px 0;
  background: #f4f3ef;
}

.cta-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
}

.cta-title {
  margin: 0;
  font-size: 18px;
  letter-spacing: 0.08em;
  font-weight: 600;
}

.cta-sub {
  margin: 10px 0 0;
  color: #0c0b06;
  font-size: 12px;
  line-height: 1.7;
}

.cta-btn {
  height: 36px;
  min-width: 150px;
  padding: 0 26px;
  border: none;
  border-radius: 0;
  background: #b08a4a;
  color: #fff;
  font-size: 11px;
  letter-spacing: 0.14em;
  cursor: pointer;
}

.cta-btn:hover {
  filter: brightness(0.96);
}

@media (max-width: 1000px) {
  .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 0 12px;
    box-sizing: border-box;
  }
}

@media (max-width: 720px) {
  .container {
    padding: 0 12px;
  }

  .page-hero-inner {
    height: 96px;
  }

  .blog-inner {
    padding: 34px 0 44px;
  }

  .grid {
    grid-template-columns: 1fr;
    gap: 18px;
    padding: 0 12px;
    box-sizing: border-box;
  }

  .cta-inner {
    flex-direction: column;
    align-items: flex-start;
  }

  .cta-btn {
    width: 100%;
    max-width: 220px;
  }
}
</style>
